import React from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { useIdleTimer } from 'react-idle-timer';
import { Outlet, useLoaderData } from 'react-router-dom';
import { loadUnAnsweredQnAs } from 'api/api';
import { LOAD_MESSAGES_INTERVAL } from 'context/msgContext';
import { getAssignedPatients, logout, showAlert, useAppContext } from '../../context/appContext';
import { AuthUser, Patient } from '../../types/types';
import {
  getCookie,
  AUTH_TOKEN_STORE_KEY,
  storage,
  AUTH_USER_STORE_KEY,
  USER_COMING_FROM_STORE_KEY,
  isNativeApp,
  isRole,
  updateFaviconWithCount,
  sessionTStorage,
} from '../../utils/utils';
import readAWSS3File from '../../utils/readAWSS3File';
import Alert from '../Alert/Alert';
import Navbar from '../Navbar/Navbar';
import useCheckInternet from '../../hooks/useCheckInternet';

const IDLE_TIMEOUT = !isNativeApp() ? 1000 * 60 * 30 : 1000 * 60 * 60 * 24 * 7;

export const patientsQuery = (user: AuthUser | null, params?: Record<string, string>) => ({
  queryKey: ['patients', params],
  queryFn: () => getAssignedPatients(user, params),
});

export const unAnsweredQnAsQuery = (patients?: Array<Patient>) => ({
  queryKey: ['unanswered-QnAs', patients],
  queryFn: () => loadUnAnsweredQnAs(patients),
});

export function loader(queryClient: QueryClient) {
  return async () => {
    const user = JSON.parse(storage.get(AUTH_USER_STORE_KEY) || '{}');

    if (getCookie(AUTH_TOKEN_STORE_KEY) && Object.keys(user).length > 0) {
      const patients =
        queryClient.getQueryData(patientsQuery(user).queryKey) ??
        (await queryClient.fetchQuery(patientsQuery(user)));

      return { user, patients } as {
        user: AuthUser | null;
        patients: Patient[];
      };
    }

    sessionTStorage.set(
      USER_COMING_FROM_STORE_KEY,
      `${window.location.pathname}${window.location.search}`,
    );

    logout(user);

    return null;
  };
}

function Layout() {
  const [{ user: authUser, alert }, dispatch] = useAppContext();
  const data = useLoaderData() as Awaited<ReturnType<ReturnType<typeof loader>>>;
  const hasInternet = useCheckInternet();

  const { user, patients } = data || {};

  const { data: unReadMessagesCounter } = useQuery({
    ...unAnsweredQnAsQuery(patients),
    refetchInterval: LOAD_MESSAGES_INTERVAL,
    // refetchOnWindowFocus: 'always',
    refetchIntervalInBackground: true,
    enabled: !!(patients?.length && !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')),
  });

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: () => logout(),
    onActive: () => false,
    debounce: 250,
  });

  React.useEffect(() => {
    showAlert(dispatch, {
      show: !hasInternet,
      message: `Sorry! Looks like you are currently Offline. Could not connect to River!`,
      type: 'ERROR',
    });
  }, [dispatch, hasInternet]);

  React.useEffect(() => {
    if (!user) {
      return;
    }

    dispatch({ type: 'SET_AUTH_USER', payload: user });

    (async function syncAvatarPhoto() {
      if (user.signedAvatar) {
        return;
      }
      const userPhoto = await readAWSS3File('avatar', user.profilePicture);
      dispatch({ type: 'SET_AUTH_USER', payload: { ...user, signedAvatar: userPhoto } });
    })();
  }, [dispatch, user]);

  /**
   * Set locally stored patients to app state
   */
  React.useEffect(() => {
    if (patients) {
      dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: patients });
    }
  }, [dispatch, patients]);

  React.useEffect(() => {
    if (unReadMessagesCounter) {
      updateFaviconWithCount(unReadMessagesCounter.length);
      dispatch({ type: 'SET_UNREAD_MESSAGES', payload: unReadMessagesCounter });
    }
  }, [dispatch, unReadMessagesCounter]);

  return (
    <div className="min-h-full flex flex-col">
      <Alert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onAction={() => showAlert(dispatch)}
        onActionMessage="Close"
      />

      <Navbar user={authUser} logout={() => logout(authUser)} />

      <main className="relative flex-auto h-full w-full px-4 tablet:px-10 py-8">
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;
